import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom'; 
import { Input } from "@/Components/ui/input";
import { Button } from "@/Components/ui/button";
import { ChevronRight, ArrowLeft } from "lucide-react";
import axiosInstance from '../utils/axiosConfig';

const ResetPassword = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [token, setToken] = useState('');
    const { resettoken } = useParams();
    const [formData, setFormData] = useState({
        password: '',
        confirmPassword: ''
    });

    useEffect(() => { 
        if (resettoken) {
            setToken(resettoken);
        } else {
            setError('Ongeldige of verlopen reset link.');
        }
    }, [resettoken]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');

        if (formData.password !== formData.confirmPassword) {
            setError('Wachtwoorden komen niet overeen');
            setIsLoading(false);
            return;
        }

        if (formData.password.length < 8) {
            setError('Wachtwoord moet minimaal 8 karakters lang zijn');
            setIsLoading(false);
            return;
        }

        try {
            await axiosInstance.post('/auth/ResetPassword', {
                token: token,
                newPassword: formData.password
            });

            setSuccess('Je wachtwoord is succesvol gewijzigd. Je wordt doorgestuurd naar de login pagina.');

            // Redirect na 3 seconden
            setTimeout(() => {
                navigate('/');
            }, 3000);

        } catch (err) {
            console.error('Reset password error:', err);

            if (err.response) {
                setError(err.response.data || 'Er is iets misgegaan bij het resetten van je wachtwoord.');
            } else if (err.request) {
                setError('Kan geen verbinding maken met de server.');
            } else {
                setError('Er is iets misgegaan bij het resetten van je wachtwoord.');
            }
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="max-w-md w-full space-y-6 p-8 bg-white rounded-lg shadow-lg">
                {/* Header Section */}
                <div className="text-center">
                    <h2 className="text-3xl font-inter text-ps-blue-dark">
                        Nieuw wachtwoord
                    </h2>
                    <p className="mt-2 text-sm text-gray-600 font-inter">
                        Voer je nieuwe wachtwoord in
                    </p>
                </div>

                {/* Form Section */}
                <form onSubmit={handleSubmit} className="mt-8 space-y-6">
                    {/* Error Display */}
                    {error && (
                        <div className="text-red-600 text-sm text-center bg-red-50 p-3 rounded font-inter">
                            {error}
                        </div>
                    )}

                    {/* Success Display */}
                    {success && (
                        <div className="text-green-600 text-sm text-center bg-green-50 p-3 rounded font-inter">
                            {success}
                        </div>
                    )}

                    {/* Password Fields */}
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-inter text-ps-blue-dark mb-2">
                                Nieuw wachtwoord
                            </label>
                            <Input
                                type="password"
                                name="password"
                                required
                                value={formData.password}
                                onChange={handleChange}
                                className="w-full p-2 border rounded bg-white font-inter"
                                placeholder="••••••••"
                                minLength={8}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-inter text-ps-blue-dark mb-2">
                                Bevestig wachtwoord
                            </label>
                            <Input
                                type="password"
                                name="confirmPassword"
                                required
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                className="w-full p-2 border rounded bg-white font-inter"
                                placeholder="••••••••"
                                minLength={8}
                            />
                        </div>
                    </div>

                    {/* Submit Button */}
                    <div>
                        <Button
                            type="submit"
                            disabled={isLoading || !token}
                            className="w-full bg-ps-blue-dark hover:opacity-80 text-white flex items-center justify-center gap-2 h-12 font-inter"
                        >
                            {isLoading ? 'Bezig met opslaan...' : 'Wachtwoord wijzigen'}
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                    </div>

                    {/* Back to Login */}
                    <div className="text-center">
                        <Button
                            type="button"
                            variant="link"
                            onClick={() => navigate('/login')}
                            className="text-ps-blue-dark hover:text-ps-blue font-medium flex items-center justify-center gap-2"
                        >
                            <ArrowLeft className="h-4 w-4" />
                            Terug naar inloggen
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ResetPassword;